import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { path, pick } from 'ramda';

import { setFundsTransferConfig } from '@interlayer/js/app/store/reducers/FundsTransfer/actions';

import { fetchRemainingDepositLimits } from 'js/app/components/main/FundsTransfer/server';
import { useFundsTransferSelector } from 'js/app/store/hooks/useFundsTransferSelector';
import { useDataFetch } from 'js/hooks/useDataFetch';
import { useFormatter } from 'js/hooks/useFormatter';
import { useUserSelector } from 'js/store/hooks';

export const useRemainingDepositLimits = () => {
    const { balance = {}, currencyCode } = useUserSelector(pick(['balance', 'currencyCode']));
    const ageVerificationStatus = useUserSelector(path(['userDetails', 'ageVerificationStatus']));
    const dispatch = useDispatch();
    const { formatCurrency } = useFormatter();
    const { fundsTransferConfig } = useFundsTransferSelector();
    const clubBalance = balance?.balances?.retail_deposits_balance?.amount || '0';

    const handlerFormatCurrency = useCallback(
        (amount = '0') => formatCurrency(amount, { currencyCode, precision: 2 }),
        [currencyCode, formatCurrency]
    );

    const onResponseRemainingDepositLimits = useCallback(({ data }) => {
        const remainingLimits = [];
        const configuredLimits = [0, 0, 0];
        const allowedByAgeVerification = ageVerificationStatus !== 'review' && ageVerificationStatus !== 'inprocess';

        for (let i = 0; i < data.limitValues.length; i++) {
            if (data.limitValues[i].limitData.length) {
                remainingLimits.push(data.limitValues[i].limitData[0].remainingLimit);
                configuredLimits[i] = data.limitValues[i].limitData[0].configuredLimit;
            }
        }

        remainingLimits.push(clubBalance);

        const allowedToTransfer = Math.min(...remainingLimits);

        const parseData = {
            isLoaded: true,
            clubBalance: handlerFormatCurrency(clubBalance),
            remainingLimits,
            allowedToTransfer: {
                value: allowedToTransfer,
                label: handlerFormatCurrency(allowedToTransfer)
            },
            configuredDailyLimit: handlerFormatCurrency(configuredLimits[0]),
            configuredMonthlyLimit: handlerFormatCurrency(configuredLimits[1]),
            configuredWeeklyLimit: handlerFormatCurrency(configuredLimits[2]),
            allowedByAgeVerification,
            shouldShowFundsTransferPrompt: allowedToTransfer > 0 && allowedByAgeVerification
        };

        dispatch(setFundsTransferConfig(parseData));

        return parseData;
    }, [clubBalance, dispatch, handlerFormatCurrency, ageVerificationStatus]);

    return useDataFetch({
        initialIsLoading: !fundsTransferConfig.isLoaded,
        fetch: fetchRemainingDepositLimits,
        initialData: fundsTransferConfig,
        onResponse: onResponseRemainingDepositLimits
    });
};
